/**
 * PresentationPills — пилюли для category='presentation' в футере виджета.
 * Рендерится внутри .gm__pills когда `isPresentation === true`, заменяя
 * image/video/audio-пилюли на presentation-specific.
 *
 * Пилюли:
 *   - Шаблон  (create only, скрыт если юзер грузит свой PPTX в upload-area) —
 *             popover с 4 встроенными вариантами
 *   - Слайдов (create only) — popover с фикс-набором [AI, 3, 5, 7, 10, 15]
 *   - Картинки (always)     — button, открывает PresentationImagesSheet
 *
 * Свой PPTX как шаблон загружается в upload-area через PresentationControls —
 * там видно сразу, не спрятано в popover.
 *
 * Matches паттерн остальных категорий: primary inputs в upload-area,
 * settings в footer pills.
 */
import { useCallback, useContext, useRef, useState } from 'react'
import BottomBarPopover from './BottomBarPopover'
import PresentationImagesSheet from './PresentationImagesSheet'
import Sheet from '../../../components/Sheet'
import AnchoredPopover, { useIsDesktopPopover } from '../../../components/AnchoredPopover'
import GammaThemesGallery from './GammaThemesGallery'
import ModelIcon from '../../../components/ModelIcon'
import GammaHeaderFooterSheet from './GammaHeaderFooterSheet'
import { hfSettingsPatch, hfStateFromSettings, hfSummaryParts, type GammaHfState } from '../utils/gammaHf'
import ToolsSubmenu from './ToolsSubmenu'
import { AspectRatioIcon } from '../../../components/AspectRatioIcon'
import { BbarSectionMode } from './bbarSectionContext'
import { useGenerate } from '../../../context/GenerateContext'
import {
	GAMMA_EXPORT_OPTIONS,
	GAMMA_IMAGE_MODEL_OPTIONS,
	GAMMA_IMAGE_SOURCE_OPTIONS,
	GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE,
	GAMMA_LANGUAGE_OPTIONS,
	GAMMA_TEXT_AMOUNT_OPTIONS,
	GAMMA_TEXT_MODE_OPTIONS,
	GAMMA_TONE_OPTIONS,
} from '../utils/gammaOptions'
import type { PresentationSettings, GammaImageSource } from '../hooks/useGenerateSettings'

const DEFAULT_PRES: PresentationSettings = { mode: 'edit' }

// Соцсетевые форматы (1:1/4:5/9:16) — готовые карусели и сторис.
const GAMMA_DIMENSIONS_OPTIONS = [
	{ value: '16x9', label: '16:9' },
	{ value: '4x3', label: '4:3' },
	{ value: '1x1', label: '1:1' },
	{ value: '4x5', label: '4:5' },
	{ value: '9x16', label: '9:16' },
]

export default function PresentationPills() {
	// Top-level диспатчер по pres.mode (не по selectedModel, т.к. mode — source
	// of truth, см. PresentationControls). При смене режима один sub-компонент
	// размонтируется, другой монтируется → стабильный порядок хуков в каждом.
	const { settings } = useGenerate()
	const mode = settings.presentation?.mode ?? 'create'
	return mode === 'create' ? <GammaPills /> : <UmnikPills />
}

/** Строка-пикер секции единой панели настроек (паттерн Suno «Стиль ›»/
 *  «Голос ›»): название + текущее значение справа + шеврон. Вложенная панель
 *  (ToolsSubmenu) или шторка передаётся детьми — строка лишь «транспорт». */
function SectionNavRow({
	rowRef,
	icon,
	label,
	value,
	open,
	onToggle,
	disabled,
	children,
}: {
	rowRef?: React.RefObject<HTMLButtonElement | null>
	icon: React.ReactNode
	label: string
	value: string
	open: boolean
	onToggle: () => void
	disabled?: boolean
	children?: React.ReactNode
}) {
	return (
		<div className="bbar-section bbar-section--nav">
			<button
				ref={rowRef}
				type="button"
				className={`bbar-navrow${open ? ' bbar-navrow--open' : ''}`}
				onClick={onToggle}
				disabled={disabled}
				aria-expanded={open}
			>
				<span className="bbar-navrow__icon" aria-hidden="true">{icon}</span>
				<span className="bbar-navrow__label">{label}</span>
				<span className="bbar-navrow__value">{value}</span>
				<span className="bbar-navrow__chevron" aria-hidden="true" />
			</button>
			{children}
		</div>
	)
}

function GammaPills() {
	const { settings, userUpdateSetting: updateSetting, isLoading } = useGenerate()
	// Section-режим: рендеримся внутри единой панели настроек (SettingsToolsPill)
	// секциями/строками-пикерами вместо ряда пилюль.
	const asSection = useContext(BbarSectionMode)
	const pres = settings.presentation ?? DEFAULT_PRES

	const setPres = useCallback(
		(patch: Partial<PresentationSettings>) => {
			updateSetting('presentation', { ...pres, ...patch })
		},
		[pres, updateSetting],
	)

	const numCards = pres.gammaNumCards ?? 5
	const language = pres.gammaLanguage ?? 'ru'
	const imageSource = pres.gammaImageSource ?? 'aiGenerated'
	const dimensions = pres.gammaDimensions ?? '16x9'
	const textAmount = pres.gammaTextAmount ?? 'medium'
	const tone = pres.gammaTone ?? ''
	const audience = pres.gammaAudience || ''
	const additionalInstructions = pres.gammaAdditionalInstructions || ''

	const languageLabel =
		GAMMA_LANGUAGE_OPTIONS.find((o) => o.value === language)?.label || language
	const imageSourceLabel =
		GAMMA_IMAGE_SOURCE_OPTIONS.find((o) => o.value === imageSource)?.label || imageSource
	const textAmountLabel =
		GAMMA_TEXT_AMOUNT_OPTIONS.find((o) => o.value === textAmount)?.label || textAmount
	const toneLabel =
		GAMMA_TONE_OPTIONS.find((o) => o.value === tone)?.label || 'Тон'

	const [instructionsOpen, setInstructionsOpen] = useState(false)
	const [draftInstructions, setDraftInstructions] = useState(additionalInstructions)
	const imageStyle = pres.gammaImageStyle || ''
	const [imageStyleOpen, setImageStyleOpen] = useState(false)
	const [draftImageStyle, setDraftImageStyle] = useState(imageStyle)
	// Драфт сеется при ОТКРЫТИИ (не эффектом-синком): внешние изменения
	// настройки при закрытом шите просто попадут в драфт при следующем открытии.
	const openImageStyle = () => {
		if (isLoading) return
		setDraftImageStyle(imageStyle)
		setImageStyleOpen(true)
	}
	const [audienceOpen, setAudienceOpen] = useState(false)
	const [draftAudience, setDraftAudience] = useState(audience)
	// Селект модели картинок: строка-пикер + ToolsSubmenu (как «Тема»).
	const [imageModelOpen, setImageModelOpen] = useState(false)
	const imageModelRowRef = useRef<HTMLButtonElement | null>(null)
	const imageModelLabel =
		(pres.gammaImageModel ?? '') === ''
			? 'Авто'
			: GAMMA_IMAGE_MODEL_OPTIONS.find((o) => o.value === pres.gammaImageModel)?.label ||
				pres.gammaImageModel ||
				'Авто'
	// Колонтитулы: черновик живёт в самом шите (монтируется на открытие),
	// здесь — конвертеры gammaHf (6 слотов + чтение легаси-полей).
	const [hfOpen, setHfOpen] = useState(false)
	const hfParts = hfSummaryParts(pres)
	const hfValue = hfStateFromSettings(pres)
	const saveHf = (v: GammaHfState) => setPres(hfSettingsPatch(v))
	// Callback-ref + useState вместо useRef: при первом mount'e юзер
	// мог не успеть кликнуть кнопку до того как ref присвоится, но если
	// клик случается ДО ref-attach (rare race) — useRef.current будет null,
	// `anchorEl={ref.current}` передаст null, GammaThemePicker увидит
	// `usePopover = isDesktop && !!anchorEl = false` → откроется Sheet bottom
	// вместо AnchoredPopover. Через useState callback-ref React делает
	// re-render как только button mounted и anchorEl всегда валиден.
	const [themeBtnEl, setThemeBtnEl] = useState<HTMLButtonElement | null>(null)
	const [themeOpen, setThemeOpen] = useState(false)
	// Якорь строки «Тема» в section-режиме: сабменю позиционируется от панели.
	const themeRowRef = useRef<HTMLButtonElement | null>(null)

	// Черновики аудитории и инструкций следуют за настройкой, пока их окно закрыто (сброс, смена извне). Подстройка во
	// время рендера, а не эффектом: тот же результат без лишнего прохода отрисовки (react-hooks/set-state-in-effect).
	const [audienceSeen, setAudienceSeen] = useState({ value: audience, open: audienceOpen })
	if (audienceSeen.value !== audience || audienceSeen.open !== audienceOpen) {
		setAudienceSeen({ value: audience, open: audienceOpen })
		if (!audienceOpen) setDraftAudience(audience)
	}
	const [instructionsSeen, setInstructionsSeen] = useState({ value: additionalInstructions, open: instructionsOpen })
	if (instructionsSeen.value !== additionalInstructions || instructionsSeen.open !== instructionsOpen) {
		setInstructionsSeen({ value: additionalInstructions, open: instructionsOpen })
		if (!instructionsOpen) setDraftInstructions(additionalInstructions)
	}

	return (
		<>
			{asSection ? (
				/* Строка-пикер «Тема ›» + галерея во вложенной панели сбоку
				   (ToolsSubmenu): AnchoredPopover здесь не годится — его клики
				   основная панель считает «кликом мимо» и закрывается. */
				<SectionNavRow
					rowRef={themeRowRef}
					icon={<ThemeIcon />}
					label="Тема"
					value={pres.gammaThemeId ? (pres.gammaThemeName || pres.gammaThemeId) : 'Авто'}
					open={themeOpen}
					onToggle={() => !isLoading && setThemeOpen((v) => !v)}
					disabled={isLoading}
				>
					<ToolsSubmenu
						open={themeOpen}
						anchorRef={themeRowRef}
						onClose={() => setThemeOpen(false)}
						width={430}
						className="gm-tools-submenu--gamma-theme"
					>
						<div className="gm-tools-submenu__title">Тема оформления</div>
						<div className="gm-tools-submenu__body">
							<GammaThemesGallery
								selectedThemeId={pres.gammaThemeId}
								onSelectTheme={(id, name) => {
									setPres({ gammaThemeId: id, gammaThemeName: name })
									setThemeOpen(false)
								}}
							/>
						</div>
					</ToolsSubmenu>
				</SectionNavRow>
			) : (
				<>
					{/* Тема — pill-button с AnchoredPopover (desktop) или Sheet (mobile).
					    Активный state когда тема выбрана (не «Авто»). */}
					<div className="bbar-pill-wrap">
						<button
							ref={setThemeBtnEl}
							type="button"
							className={
								'bbar-pill' +
								(pres.gammaThemeId ? ' bbar-pill--active' : '') +
								(isLoading ? ' bbar-pill--disabled' : '')
							}
							onClick={() => !isLoading && setThemeOpen(true)}
							disabled={isLoading}
							title={
								pres.gammaThemeId
									? `Тема: ${pres.gammaThemeName || pres.gammaThemeId} · клик чтобы сменить`
									: 'Готовое визуальное оформление: цвета, шрифты, layout. Если не выбрать — Gamma подберёт сама под содержание промпта.'
							}
						>
							<span className="bbar-pill__icon" aria-hidden="true">
								<ThemeIcon />
							</span>
							<span className="bbar-pill__label">
								{pres.gammaThemeId ? (pres.gammaThemeName || 'Тема выбрана') : 'Тема'}
							</span>
						</button>
					</div>
					<GammaThemePicker
						open={themeOpen}
						onClose={() => setThemeOpen(false)}
						selectedThemeId={pres.gammaThemeId}
						onSelectTheme={(id, name) => {
							setPres({ gammaThemeId: id, gammaThemeName: name })
							setThemeOpen(false)
						}}
						anchorEl={themeBtnEl}
					/>
				</>
			)}
			<BottomBarPopover
				tooltip="Сколько слайдов сгенерировать. Больше слайдов — подробнее раскрыта тема, но дольше генерация и больше токенов уйдёт."
				sectionTitle="Слайды"
				icon={<SlidesIcon />}
				label={`${numCards} слайд${numCards === 1 ? '' : numCards >= 2 && numCards <= 4 ? 'а' : 'ов'}`}
				slider={{
					// Поштучно, а не фикс-набором: бэк принимает 1..50 (ge/le в
					// PresentationsGenerateRequest), цена честно множится на число.
					min: 1,
					max: 50,
					step: 1,
					value: numCards,
					onChange: (v) => setPres({ gammaNumCards: v }),
				}}
				disabled={isLoading}
			/>
			<BottomBarPopover
				tooltip="Язык на котором Gamma напишет текст слайдов. Если промпт на русском, а здесь English — содержание переведётся на английский."
				sectionTitle="Язык"
				icon={<LanguageIcon />}
				label={languageLabel}
				options={GAMMA_LANGUAGE_OPTIONS}
				value={language}
				onChange={(v) => setPres({ gammaLanguage: v })}
				disabled={isLoading}
			/>
			<BottomBarPopover
				tooltip="Как трактовать введённый текст. «Сгенерировать» — промпт это тема, Gamma напишет содержание сама. «Сжать мой текст» — вставьте длинный текст, Gamma сожмёт его в слайды. «Как есть» — ваш текст ляжет на слайды без переписывания."
				sectionTitle="Текст"
				icon={<TextModeIcon />}
				label={GAMMA_TEXT_MODE_OPTIONS.find((o) => o.value === (pres.gammaTextMode ?? 'generate'))?.label || 'Сгенерировать'}
				options={GAMMA_TEXT_MODE_OPTIONS}
				value={pres.gammaTextMode ?? 'generate'}
				onChange={(v) => setPres({ gammaTextMode: v as PresentationSettings['gammaTextMode'] })}
				disabled={isLoading}
			/>
			<BottomBarPopover
				tooltip="Откуда брать иллюстрации для слайдов: AI-генерация (уникальные), стоковые (Pexels), GIF (Giphy), иконки, заглушки или вовсе без картинок."
				sectionTitle="Источник картинок"
				icon={<ImagesIcon />}
				label={imageSourceLabel}
				options={GAMMA_IMAGE_SOURCE_OPTIONS}
				value={imageSource}
				onChange={(v) => setPres({ gammaImageSource: v as GammaImageSource })}
				disabled={isLoading}
			/>
			{/* Модель картинок: в панели — строка-селект с ToolsSubmenu (13 чипов
			    раздували секцию, решение владельца 2026-08-14); в ряду пилюль
			    BottomBarPopover сам рендерит дроп-список — уже селект. */}
			{imageSource === 'aiGenerated' && (asSection ? (
				<SectionNavRow
					rowRef={imageModelRowRef}
					icon={<ImagesIcon />}
					label="Модель картинок"
					value={imageModelLabel}
					open={imageModelOpen}
					onToggle={() => !isLoading && setImageModelOpen((v) => !v)}
					disabled={isLoading}
				>
					<ToolsSubmenu
						open={imageModelOpen}
						anchorRef={imageModelRowRef}
						onClose={() => setImageModelOpen(false)}
						width={300}
						className="gamma-model-submenu"
					>
						<div className="gamma-model-select">
							{GAMMA_IMAGE_MODEL_OPTIONS.map((o) => {
								const surcharge = GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE[o.tier] || 0
								const active = (pres.gammaImageModel ?? '') === o.value
								return (
									<button
										key={o.value}
										type="button"
										className={
											'bbar-popover__option' +
											(active ? ' bbar-popover__option--active' : '')
										}
										onClick={() => {
											setPres({ gammaImageModel: o.value || undefined })
											setImageModelOpen(false)
										}}
									>
										<span className="bbar-popover__option-icon">
											<ModelIcon icon={o.iconUrl} name={o.label} size={18} className="gamma-model-logo" />
										</span>
										<span className="bbar-popover__option-label">{o.label}</span>
										{surcharge > 0 && (
											<span className="gamma-model-select__price">+{surcharge}✦/сл</span>
										)}
										{active && <span className="check-badge" aria-hidden="true" />}
									</button>
								)
							})}
						</div>
					</ToolsSubmenu>
				</SectionNavRow>
			) : (
				<BottomBarPopover
					tooltip="Какая нейросеть рисует картинки слайдов. Премиум-модели дают качество выше, но дороже: надбавка указана за каждый слайд."
					icon={<ImagesIcon />}
					label={imageModelLabel}
					options={GAMMA_IMAGE_MODEL_OPTIONS.map((o) => {
						const surcharge = GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE[o.tier] || 0
						return {
							value: o.value,
							label: surcharge > 0 ? `${o.label} · +${surcharge}✦/сл` : o.label,
							icon: (
								<ModelIcon icon={o.iconUrl} name={o.label} size={16} className="gamma-model-logo" />
							),
						}
					})}
					value={pres.gammaImageModel ?? ''}
					onChange={(v) => setPres({ gammaImageModel: v || undefined })}
					disabled={isLoading}
				/>
			))}
			{imageSource === 'aiGenerated' && (asSection ? (
				<SectionNavRow
					icon={<ImagesIcon />}
					label="Стиль картинок"
					value={imageStyle ? (imageStyle.length > 16 ? `${imageStyle.slice(0, 16)}…` : imageStyle) : 'Авто'}
					open={imageStyleOpen}
					onToggle={openImageStyle}
					disabled={isLoading}
				/>
			) : (
				<div className="bbar-pill-wrap">
					<button
						type="button"
						className={
							'bbar-pill' +
							(imageStyle ? ' bbar-pill--active' : '') +
							(isLoading ? ' bbar-pill--disabled' : '')
						}
						onClick={openImageStyle}
						disabled={isLoading}
						title={
							imageStyle
								? `Стиль картинок: ${imageStyle.slice(0, 60)}${imageStyle.length > 60 ? '…' : ''}`
								: 'Опишите визуальный стиль AI-картинок: «акварель, пастельные тона», «минимализм, монохром», «фотореализм, тёплый свет».'
						}
					>
						<span className="bbar-pill__icon" aria-hidden="true"><ImagesIcon /></span>
						<span className="bbar-pill__label">
							{imageStyle ? 'Стиль задан' : 'Стиль картинок'}
						</span>
					</button>
				</div>
			))}
			{imageStyleOpen && (
				<GammaImageStyleSheet
					open={imageStyleOpen}
					value={draftImageStyle}
					onChange={setDraftImageStyle}
					onClose={() => {
						const trimmed = draftImageStyle.trim()
						setPres({ gammaImageStyle: trimmed || undefined })
						setImageStyleOpen(false)
					}}
				/>
			)}
			<BottomBarPopover
				tooltip="Соотношение сторон слайда. 16:9 — современный широкоформатный (для экранов и проекторов), 4:3 — классический (печать, старые проекторы, A4)."
				sectionTitle="Формат слайда"
				icon={<AspectRatioIcon ratio={dimensions.replace('x', ':')} />}
				label={dimensions.replace('x', ':')}
				options={GAMMA_DIMENSIONS_OPTIONS.map((o) => ({
					...o,
					icon: <AspectRatioIcon ratio={o.value.replace('x', ':')} />,
				}))}
				value={dimensions}
				onChange={(v) => setPres({ gammaDimensions: v as PresentationSettings['gammaDimensions'] })}
				disabled={isLoading}
			/>
			<BottomBarPopover
				tooltip="Сколько текста размещать на одном слайде. Кратко — bullet-points (для презентации с речью), Средне — баланс, Подробно — развёрнутые абзацы (для самостоятельного чтения)."
				sectionTitle="Объём текста"
				icon={<TextAmountIcon />}
				label={textAmountLabel}
				options={GAMMA_TEXT_AMOUNT_OPTIONS}
				value={textAmount}
				onChange={(v) =>
					setPres({ gammaTextAmount: v as PresentationSettings['gammaTextAmount'] })
				}
				disabled={isLoading}
			/>
			<BottomBarPopover
				tooltip="Стиль и интонация подачи материала. Формальный — для делового контекста, Дружелюбный — для маркетинга, Убедительный — для продаж, Обучающий — для лекций."
				sectionTitle="Тон"
				icon={<ToneIcon />}
				label={toneLabel}
				options={GAMMA_TONE_OPTIONS}
				value={tone}
				onChange={(v) => setPres({ gammaTone: v || undefined })}
				disabled={isLoading}
			/>
			{asSection ? (
				<SectionNavRow
					icon={<HfIcon />}
					label="Колонтитулы"
					value={hfParts.length ? hfParts.join(' + ') : 'Нет'}
					open={hfOpen}
					onToggle={() => !isLoading && setHfOpen(true)}
					disabled={isLoading}
				/>
			) : (
				<div className="bbar-pill-wrap">
					<button
						type="button"
						className={
							'bbar-pill' +
							(hfParts.length ? ' bbar-pill--active' : '') +
							(isLoading ? ' bbar-pill--disabled' : '')
						}
						onClick={() => !isLoading && setHfOpen(true)}
						disabled={isLoading}
						title="Подпись и номер на каждом слайде — как в PowerPoint: название компании, «Конфиденциально», дата."
					>
						<span className="bbar-pill__icon" aria-hidden="true"><HfIcon /></span>
						<span className="bbar-pill__label">
							{hfParts.length ? `Колонтитулы: ${hfParts.join(' + ')}` : 'Колонтитулы'}
						</span>
					</button>
				</div>
			)}
			{hfOpen && (
				<GammaHeaderFooterSheet
					open={hfOpen}
					value={hfValue}
					onSave={saveHf}
					onClose={() => setHfOpen(false)}
				/>
			)}
			<BottomBarPopover
				tooltip="Формат готового файла. PPTX — правится в PowerPoint/Keynote, PDF — для отправки и печати, PNG — картинки слайдов."
				sectionTitle="Файл"
				icon={<ExportIcon />}
				label={(pres.gammaExportAs ?? 'pptx').toUpperCase()}
				options={GAMMA_EXPORT_OPTIONS}
				value={pres.gammaExportAs ?? 'pptx'}
				onChange={(v) => setPres({ gammaExportAs: v as PresentationSettings['gammaExportAs'] })}
				disabled={isLoading}
			/>
			{/* Аудитория — в панели строкой-пикером со значением (шит общий и
			    открывается ПОВЕРХ панели: .sheet-backdrop она игнорирует),
			    в ряду пилюль — прежняя кнопка. */}
			{asSection ? (
				<SectionNavRow
					icon={<AudienceIcon />}
					label="Аудитория"
					value={audience ? (audience.length > 16 ? `${audience.slice(0, 16)}…` : audience) : 'Не задана'}
					open={audienceOpen}
					onToggle={() => !isLoading && setAudienceOpen(true)}
					disabled={isLoading}
				/>
			) : (
			<div className="bbar-pill-wrap">
				<button
					type="button"
					className={
						'bbar-pill' +
						(audience ? ' bbar-pill--active' : '') +
						(isLoading ? ' bbar-pill--disabled' : '')
					}
					onClick={() => !isLoading && setAudienceOpen(true)}
					disabled={isLoading}
					title={
						audience
							? `Аудитория: ${audience.slice(0, 60)}${audience.length > 60 ? '…' : ''}`
							: 'Кому адресована презентация. Gamma подстроит сложность, примеры и подачу. Например: «инвесторы B2B-сектора», «школьники 5-7 класс», «маркетологи стартапа».'
					}
				>
					<span className="bbar-pill__icon" aria-hidden="true">
						<AudienceIcon />
					</span>
					<span className="bbar-pill__label">
						{audience ? 'Аудитория задана' : 'Аудитория'}
					</span>
				</button>
			</div>
			)}
			{audienceOpen && (
				<GammaAudienceSheet
					open={audienceOpen}
					value={draftAudience}
					onChange={setDraftAudience}
					onClose={() => {
						const trimmed = draftAudience.trim()
						setPres({ gammaAudience: trimmed || undefined })
						setAudienceOpen(false)
					}}
				/>
			)}
			{/* Инструкции — в панели строкой-пикером (шит общий, поверх панели),
			    в ряду пилюль — прежняя кнопка. */}
			{asSection ? (
				<SectionNavRow
					icon={<InstructionsIcon />}
					label="Инструкции"
					value={
						additionalInstructions
							? (additionalInstructions.length > 16
								? `${additionalInstructions.slice(0, 16)}…`
								: additionalInstructions)
							: 'Не заданы'
					}
					open={instructionsOpen}
					onToggle={() => !isLoading && setInstructionsOpen(true)}
					disabled={isLoading}
				/>
			) : (
			<div className="bbar-pill-wrap">
				<button
					type="button"
					className={
						'bbar-pill' +
						(additionalInstructions ? ' bbar-pill--active' : '') +
						(isLoading ? ' bbar-pill--disabled' : '')
					}
					onClick={() => !isLoading && setInstructionsOpen(true)}
					disabled={isLoading}
					title={
						additionalInstructions
							? `Инструкции: ${additionalInstructions.slice(0, 60)}${additionalInstructions.length > 60 ? '…' : ''}`
							: 'Дополнительные требования для AI: обязательные пункты, чего избегать, особый формат. Учитывается сверх основного промпта.'
					}
				>
					<span className="bbar-pill__icon" aria-hidden="true">
						<InstructionsIcon />
					</span>
					<span className="bbar-pill__label">
						{additionalInstructions ? 'Инструкции есть' : 'Инструкции'}
					</span>
				</button>
			</div>
			)}
			{instructionsOpen && (
				<GammaInstructionsSheet
					open={instructionsOpen}
					value={draftInstructions}
					onChange={setDraftInstructions}
					onClose={() => {
						// Сохраняем при закрытии (даже по backdrop). Если юзер не хотел —
						// просто не вводил текст. Trim — пустой строкой не записываем.
						const trimmed = draftInstructions.trim()
						setPres({ gammaAdditionalInstructions: trimmed || undefined })
						setInstructionsOpen(false)
					}}
				/>
			)}
		</>
	)
}

function UmnikPills() {
	const { settings, isLoading } = useGenerate()
	const asSection = useContext(BbarSectionMode)
	const pres = settings.presentation ?? DEFAULT_PRES

	// ── Images sheet ─────────────────────────────────────────────
	const [imagesOpen, setImagesOpen] = useState(false)
	const imagesCount = pres.imageUrls?.length ?? 0

	return (
		<>
			{/* Картинки — всегда (и edit, и create). Custom pill-button вместо
			    BottomBarPopover — потому что нужен dropzone внутри, не select.
			    В панели — строка-пикер; шторка открывается ПОВЕРХ панели. */}
			{asSection ? (
				<SectionNavRow
					icon={<ImagesIcon />}
					label="Картинки"
					value={imagesCount > 0 ? `${imagesCount}/5` : 'Нет'}
					open={imagesOpen}
					onToggle={() => !isLoading && setImagesOpen(true)}
					disabled={isLoading}
				/>
			) : (
			<div className="bbar-pill-wrap">
				<button
					type="button"
					className={
						'bbar-pill' +
						(imagesCount > 0 ? ' bbar-pill--active' : '') +
						(isLoading ? ' bbar-pill--disabled' : '')
					}
					onClick={() => !isLoading && setImagesOpen(true)}
					disabled={isLoading}
					title={
						imagesCount > 0
							? `Прикреплено ${imagesCount} из 5 картинок · клик чтобы изменить`
							: 'Прикрепи свои картинки (до 5 шт.) — AI вставит их в подходящие слайды по смыслу. Если не прикрепить, AI возьмёт картинки из выбранного источника изображений.'
					}
				>
					<span className="bbar-pill__icon" aria-hidden="true"><ImagesIcon /></span>
					<span className="bbar-pill__label">
						{imagesCount > 0 ? `Картинки ${imagesCount}/5` : 'Картинки'}
					</span>
				</button>
			</div>
			)}

			<PresentationImagesSheet
				open={imagesOpen}
				onClose={() => setImagesOpen(false)}
			/>
		</>
	)
}

// ── Icons ───────────────────────────────────────────────────

function SlidesIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<rect x="3" y="4" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.3" />
			<line x1="5" y1="13" x2="11" y2="13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
		</svg>
	)
}

function ImagesIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
			<circle cx="6" cy="6.5" r="1.2" stroke="currentColor" strokeWidth="1.2" />
			<path d="M14 11L10 7L4 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	)
}

function LanguageIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3" />
			<path d="M2 8h12 M8 2c2 2 2.5 4 2.5 6s-0.5 4-2.5 6c-2-2-2.5-4-2.5-6s0.5-4 2.5-6z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	)
}


function TextAmountIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<line x1="2" y1="4" x2="14" y2="4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
			<line x1="2" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
			<line x1="2" y1="12" x2="8" y2="12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
		</svg>
	)
}

function InstructionsIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<rect x="3" y="2" width="10" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
			<line x1="6" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
			<line x1="6" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
			<line x1="6" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
		</svg>
	)
}

function TextModeIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<path d="M3 4h10M3 8h10M3 12h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
			<path d="M12.5 10.2l1.3 1.3-2.6 2.6-1.5.2.2-1.5z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
		</svg>
	)
}

function HfIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
			<path d="M4.5 10.8h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
			<circle cx="11.5" cy="10.8" r="0.9" fill="currentColor" />
		</svg>
	)
}

function ExportIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<path d="M4 2.5h5l3 3v8H4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
			<path d="M8 7v4M6.3 9.6 8 11.3l1.7-1.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	)
}

function ThemeIcon() {
	// Палитра — три кружочка разных цветов. Универсальный «тематический» символ.
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<circle cx="5" cy="6" r="3" fill="currentColor" opacity="0.9" />
			<circle cx="11" cy="6" r="3" fill="currentColor" opacity="0.55" />
			<circle cx="8" cy="11" r="3" fill="currentColor" opacity="0.3" />
		</svg>
	)
}

function ToneIcon() {
	// «Воздушные» волны — отражают речь / тон. Аналог микрофонной волны.
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<path d="M3 8 Q 5 4, 7 8 T 11 8 T 15 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
			<path d="M1 11 Q 3 9, 5 11 T 9 11 T 13 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.55" />
		</svg>
	)
}

function AudienceIcon() {
	// Силуэты людей — целевая аудитория.
	return (
		<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
			<circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" />
			<path d="M2 13 Q 2 9, 5 9 Q 8 9, 8 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
			<circle cx="11" cy="6" r="1.6" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
			<path d="M8.5 13 Q 8.5 10, 11 10 Q 13.5 10, 13.5 13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7" />
		</svg>
	)
}

// ── GammaThemePicker — adaptive popover/sheet с GammaThemesGallery ──
//
// Desktop: AnchoredPopover вплотную к pill-кнопке (как HeyGen Avatar Picker).
// Mobile: bottom Sheet (на узком экране popover режется).

interface ThemePickerProps {
	open: boolean
	onClose: () => void
	selectedThemeId: string | undefined
	onSelectTheme: (themeId: string | undefined, themeName?: string) => void
	anchorEl?: HTMLElement | null
}

function GammaThemePicker({
	open,
	onClose,
	selectedThemeId,
	onSelectTheme,
	anchorEl,
}: ThemePickerProps) {
	const isDesktop = useIsDesktopPopover()
	const usePopover = isDesktop && !!anchorEl

	const body = (
		<div className="gamma-theme-picker__body">
			<GammaThemesGallery
				selectedThemeId={selectedThemeId}
				onSelectTheme={onSelectTheme}
			/>
		</div>
	)

	if (usePopover && anchorEl) {
		return (
			<AnchoredPopover
				open={open}
				onClose={onClose}
				anchorEl={anchorEl}
				width={520}
				align="center"
				className="gamma-theme-picker gamma-theme-picker--popover"
			>
				{body}
			</AnchoredPopover>
		)
	}

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="bottom"
			title="Тема оформления"
			showHandle
			className="gamma-theme-picker"
		>
			{body}
		</Sheet>
	)
}

// ── GammaInstructionsSheet — Sheet с textarea для AdditionalInstructions ──

interface InstructionsSheetProps {
	open: boolean
	value: string
	onChange: (value: string) => void
	onClose: () => void
}

// ── GammaAudienceSheet — Sheet с input для целевой аудитории ──

interface AudienceSheetProps {
	open: boolean
	value: string
	onChange: (value: string) => void
	onClose: () => void
}

function GammaImageStyleSheet({ open, value, onChange, onClose }: AudienceSheetProps) {
	const MAX_LENGTH = 5000
	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title="Стиль картинок"
			showHandle
			className="gamma-instructions-sheet"
		>
			<div className="gamma-instructions-sheet__body">
				<p className="gamma-instructions-sheet__hint">
					Опишите визуальный стиль AI-иллюстраций — Gamma применит его ко всем
					картинкам презентации. Чем конкретнее, тем целостнее визуал.
				</p>
				<textarea
					className="gamma-instructions-sheet__textarea"
					placeholder="Например: «акварельные иллюстрации, пастельные тона, без текста на картинках» или «строгий корпоративный фотореализм, синяя палитра»"
					value={value}
					onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
					rows={4}
					autoFocus
				/>
				<div className="gamma-instructions-sheet__footer">
					<span className="gamma-instructions-sheet__counter">
						{value.length} / {MAX_LENGTH}
					</span>
					<button
						type="button"
						className="gamma-instructions-sheet__btn"
						onClick={onClose}
					>
						Готово
					</button>
				</div>
			</div>
		</Sheet>
	)
}

function GammaAudienceSheet({ open, value, onChange, onClose }: AudienceSheetProps) {
	const MAX_LENGTH = 200
	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title="Целевая аудитория"
			showHandle
			className="gamma-instructions-sheet"
		>
			<div className="gamma-instructions-sheet__body">
				<p className="gamma-instructions-sheet__hint">
					Кому адресована презентация. Gamma подстроит уровень сложности,
					терминологию, примеры и тон под эту аудиторию. Чем точнее —
					тем релевантнее результат.
				</p>
				<input
					type="text"
					className="gamma-instructions-sheet__textarea"
					style={{ minHeight: 'auto', height: 44 }}
					placeholder="Например: «инвесторы B2B-сектора», «школьники 5-7 класс», «маркетологи стартапа»"
					value={value}
					onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
					autoFocus
				/>
				<div className="gamma-instructions-sheet__footer">
					<span className="gamma-instructions-sheet__counter">
						{value.length} / {MAX_LENGTH}
					</span>
					<button
						type="button"
						className="gamma-instructions-sheet__btn"
						onClick={onClose}
					>
						Готово
					</button>
				</div>
			</div>
		</Sheet>
	)
}

function GammaInstructionsSheet({ open, value, onChange, onClose }: InstructionsSheetProps) {
	const MAX_LENGTH = 1000
	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title="Дополнительные инструкции"
			showHandle
			className="gamma-instructions-sheet"
		>
			<div className="gamma-instructions-sheet__body">
				<p className="gamma-instructions-sheet__hint">
					Дополнительные требования сверх промпта: обязательные пункты, чего
					избегать, особый формат изложения, акценты на конкретных аспектах.
					Gamma учтёт это при генерации каждого слайда.
				</p>
				<textarea
					className="gamma-instructions-sheet__textarea"
					placeholder="Например: «Обязательно отдельный слайд с метриками роста. Без юмора и эмодзи. Не упоминать конкурентов по именам. Заключение — call-to-action.»"
					value={value}
					onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
					rows={6}
					autoFocus
				/>
				<div className="gamma-instructions-sheet__footer">
					<span className="gamma-instructions-sheet__counter">
						{value.length} / {MAX_LENGTH}
					</span>
					<button
						type="button"
						className="gamma-instructions-sheet__btn"
						onClick={onClose}
					>
						Готово
					</button>
				</div>
			</div>
		</Sheet>
	)
}
